import { ApiClientProvider } from '@hostesa/api-client';
import { AuthProvider } from '@hostesa/auth-client';
import { I18nProvider } from '@hostesa/i18n';
import { HostesaErrorBoundary, ObservabilityProvider } from '@hostesa/observability';
import { StateProvider } from '@hostesa/state';
import { ErrorState } from '@hostesa/ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@hostesa/ui/styles.css';

import { APP_I18N_RESOURCES } from './i18n/resources';
import { setAuthSnapshot } from './lib/auth-bridge';
import { AuthBridge } from './lib/auth-bridge-react';
import { bootstrapApp } from './lib/bootstrap';
import { RuntimeProvider } from './lib/runtime-context';
import { createAppRouter } from './router';

/**
 * Bootstrap order (provider composition) — identical to the platform stack but
 * for the backoffice (app="backoffice", separate Sentry release):
 *   ErrorBoundary > ObservabilityProvider > StateProvider (Redux) >
 *   QueryClientProvider > ApiClientProvider > I18nProvider > AuthProvider >
 *   RuntimeProvider > AuthBridge > RouterProvider
 *
 * There is no app-wide RealtimeProvider: the backoffice is operator-level and
 * not bound to a single hotel's socket rooms.
 *
 * The 401 handler clears the session and routes to /login?next=<current>; the
 * router invalidation in <AuthBridge> then re-runs guards.
 */
function start() {
  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('Root element #root not found');

  // Build the router first so the 401 handler can navigate through it.
  let router: ReturnType<typeof createAppRouter> | null = null;

  const runtime = bootstrapApp({
    onUnauthorized: () => {
      const next = `${window.location.pathname}${window.location.search}`;
      // `to` cast: see lib/nav.ts (platform owns the global router Register).
      void router?.navigate({
        to: '/login' as never,
        search: { next } as never,
      });
    },
  });

  // Seed the synchronous auth snapshot from any token persisted in
  // sessionStorage (a page refresh). The router's session gate reads
  // `getAuthSnapshot().token` before `AuthBridge` has published the React auth
  // state, so without this a refresh would bounce to /login before
  // `AuthProvider` restores the session. `loading: true` lets the guard wait
  // for `/auth/me` to re-validate the token.
  const bootToken = runtime.sessionStore.getToken();
  if (bootToken !== null) {
    setAuthSnapshot({ token: bootToken, user: null, memberships: [], loading: true });
  }

  router = createAppRouter({ runtime });
  const appRouter = router;

  // Clear the query cache on logout (AuthProvider.onLogout) and return to login.
  const handleLogout = () => {
    runtime.queryClient.clear();
    void appRouter.navigate({ to: '/login' as never });
  };

  createRoot(rootElement).render(
    <StrictMode>
      <HostesaErrorBoundary fallback={<ErrorState data-testid="app-error-boundary" />}>
        <ObservabilityProvider
          app="backoffice"
          environment={runtime.config.environment}
          release={`backoffice@${runtime.config.releaseId}`}
        >
          <StateProvider>
            <QueryClientProvider client={runtime.queryClient}>
              <ApiClientProvider client={runtime.apiClient}>
                <I18nProvider
                  locale={runtime.config.defaultLocale}
                  i18nOptions={{ resources: APP_I18N_RESOURCES }}
                >
                  <AuthProvider
                    apiClient={runtime.apiClient}
                    sessionStore={runtime.sessionStore}
                    onLogout={handleLogout}
                  >
                    <RuntimeProvider runtime={runtime}>
                      <AuthBridge router={appRouter}>
                        <RouterProvider router={appRouter} />
                      </AuthBridge>
                    </RuntimeProvider>
                  </AuthProvider>
                </I18nProvider>
              </ApiClientProvider>
            </QueryClientProvider>
          </StateProvider>
        </ObservabilityProvider>
      </HostesaErrorBoundary>
    </StrictMode>,
  );
}

start();
